import React, { useState } from 'react';
import './FAQ.css';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  // General Questions
  {
    category: "General",
    question: "What is Swing Setup AI?",
    answer: "Swing Setup AI is an AI-powered stock screening platform designed for swing traders. We scan 3,000+ Indian stocks daily to identify high-probability trading setups using advanced technical analysis and pattern recognition algorithms."
  },
  {
    category: "General",
    question: "Who is Swing Setup AI for?",
    answer: "Swing Setup AI is designed for swing traders, positional traders, and investors who want to identify quality stock setups without spending hours manually scanning charts. Whether you're a beginner or experienced trader, our AI-powered screeners help you find opportunities faster."
  },
  {
    category: "General",
    question: "How often is the data updated?",
    answer: "Our screeners are updated daily after market hours. The stock data is refreshed to reflect the latest price movements, volumes, and technical indicators to ensure you get accurate trading signals."
  },

  // Screeners & Features
  {
    category: "Screeners & Features",
    question: "How accurate are the pattern detections?",
    answer: "Our AI-powered pattern detection has an accuracy rate of 80%+ based on historical price patterns observed over the last 3 months. However, no pattern detection system is 100% accurate, and past performance is not indicative of future results."
  },
  {
    category: "Screeners & Features",
    question: "What is the Learning Hub?",
    answer: "The Learning Hub is our educational section where you can learn about swing trading strategies, chart patterns, risk management, and stop-loss techniques. It includes detailed guides on VCP, Flag & Pennant patterns, Narrow Range strategies, and more."
  },

  // Subscription & Pricing
  {
    category: "Subscription & Pricing",
    question: "Is there a free trial available?",
    answer: "Yes, you can explore our platform with limited access to screeners. To unlock all premium features, AI-powered screeners, and detailed stock analysis, you'll need to subscribe to our premium plan."
  },
  {
    category: "Subscription & Pricing",
    question: "What payment methods do you accept?",
    answer: "We accept payments through Razorpay, which supports UPI, credit cards, debit cards, net banking, and popular wallets like Paytm, PhonePe, and Google Pay."
  },
  {
    category: "Subscription & Pricing",
    question: "How do I upgrade to Premium?",
    answer: "Click on the 'Pricing' page from the navigation menu, select your preferred plan, and complete the payment through Razorpay. Your premium access will be activated instantly after successful payment."
  },
  {
    category: "Subscription & Pricing",
    question: "Can I cancel my subscription?",
    answer: "Our subscriptions are prepaid for the selected duration (monthly/quarterly/yearly). There are no recurring charges, so you don't need to cancel. Your access will remain active until the subscription period ends."
  },
  {
    category: "Subscription & Pricing",
    question: "What happens when my subscription expires?",
    answer: "When your subscription expires, you'll be reverted to the free plan with limited access. You can renew anytime to regain full access to all premium features and screeners."
  },
  {
    category: "Subscription & Pricing",
    question: "Do you offer refunds?",
    answer: "Due to the digital nature of our service, we generally do not offer refunds once the subscription is activated. However, if you face any technical issues, please contact our support team and we'll try to resolve them."
  },

  // Technical & Account
  {
    category: "Technical & Account",
    question: "How do I create an account?",
    answer: "Click on the 'Login' button in the navigation bar. You can sign up using your email address or continue with Google authentication for quick access."
  },

  // Trading & Strategy
  {
    category: "Trading & Strategy",
    question: "Does Swing Setup AI provide buy/sell recommendations?",
    answer: "Swing Setup AI is a screening and analysis tool, not a SEBI-registered investment advisor. We identify potential trading setups based on technical patterns, but the final trading decision should always be yours. Always do your own research before trading."
  },
  {
    category: "Trading & Strategy",
    question: "What is the ideal holding period for swing trades?",
    answer: "Swing trades typically have a holding period of 3-21 days, depending on the pattern and market conditions. Our screeners are optimized for this timeframe, helping you capture short to medium-term price movements."
  },
  {
    category: "Trading & Strategy",
    question: "How should I use stop-loss with screener picks?",
    answer: "We strongly recommend using stop-loss orders for every trade. Check our Learning Hub for detailed guides on smart stop-loss strategies and position sizing based on your risk tolerance."
  }
];

const FAQ: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("All");

  const categories = ["All", ...Array.from(new Set(faqData.map(item => item.category)))];

  const filteredFAQs = activeCategory === "All"
    ? faqData
    : faqData.filter(item => item.category === activeCategory);

  const toggleFAQ = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className="faq-page">
      <div className="faq-container">
        {/* Header */}
        <div className="faq-header">
          <h1>Frequently Asked Questions</h1>
          <p>Find answers to common questions about Swing Setup AI</p>
        </div>

        {/* Category Filter */}
        <div className="faq-categories">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-btn ${activeCategory === category ? 'active' : ''}`}
              onClick={() => { setActiveCategory(category); setActiveIndex(null); }}
            >
              {category}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="faq-list">
          {filteredFAQs.map((faq, index) => (
            <div
              key={index}
              className={`faq-item ${activeIndex === index ? 'active' : ''}`}
            >
              <button
                className="faq-question"
                onClick={() => toggleFAQ(index)}
              >
                <span>{faq.question}</span>
                <span className="faq-icon">{activeIndex === index ? '−' : '+'}</span>
              </button>
              <div className={`faq-answer ${activeIndex === index ? 'show' : ''}`}>
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="faq-contact">
          <h3>Still have questions?</h3>
          <p>Can't find the answer you're looking for? Reach out to our support team.</p>
          <a href="/contact" className="contact-btn">Contact Us</a>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
